import React, { ReactNode, useCallback, useEffect, useReducer } from 'react'
import { ChainId, CHAIN_ID_NAMES } from '../../constants'
import { useEthers, useLocalStorage } from '../../hooks'
import { useBlockNumber } from '../blockNumber/context'
import { TransactionsContext } from './context'
import { DEFAULT_STORED_TRANSACTIONS, isEnqueuedOrder, StoredTransaction, TransactionToSave } from './model'
import { transactionReducer } from './reducer'

interface Props {
  children: ReactNode
}

interface OrderRequest {
  transactionHash: string
  nonce: number
}

export function TransactionProvider({ children }: Props) {
  const { account, chainId, library } = useEthers()
  const blockNumber = useBlockNumber()
  const [storage, setStorage] = useLocalStorage('transactions_v20210812')
  const [transactions, dispatch] = useReducer(transactionReducer, storage ?? DEFAULT_STORED_TRANSACTIONS)

  useEffect(() => {
    setStorage(transactions)
  }, [transactions])

  const addTransaction = useCallback(
    (transaction: TransactionToSave) => {
      dispatch({
        type: 'TRANSACTION_ADDED',
        chainId: transaction.chainId,
        from: transaction.from,
        hash: transaction.hash,
        nonce: transaction.nonce,
        submittedAt: Date.now(),
        txInfo: transaction.txInfo,
      })
    },
    [dispatch]
  )

  useEffect(() => {
    if (!chainId || !library || !blockNumber) return
    ;(transactions[chainId] ?? [])
      .filter((tx) => shouldCheck(blockNumber, tx))
      .forEach((tx) => {
        library
          .getTransactionReceipt(tx.hash)
          .then((receipt) => {
            if (receipt) {
              dispatch({
                type: 'TRANSACTION_MINED',
                chainId,
                hash: tx.hash,
                receipt,
              })
            } else {
              dispatch({
                type: 'TRANSACTION_CHECKED',
                chainId,
                hash: tx.hash,
                blockNumber,
              })
            }
          })
          .catch((error) => {
            console.error(`failed to check transaction hash: ${tx.hash}`, error)
          })
      })
  }, [chainId, library, transactions, blockNumber])

  const orders =
    chainId &&
    (transactions[chainId] ?? []).reduce((memo: OrderRequest[], transaction) => {
      if (isEnqueuedOrder(transaction)) {
        memo.push({
          transactionHash: transaction.hash,
          nonce: transaction.nonce,
        })
      }
      return memo
    }, [])
  orders?.splice(30) // Limit to 30 items

  useEffect(() => {
    let cancelled = false
    async function updateOrderStatuses() {
      if (!account || !chainId || !orders || orders.length === 0) {
        return
      }
      try {
        const res = await fetch(
          `https://${chainId === ChainId.Kovan ? 'kovan-api' : 'api'}.integral.link/api/v3/order-status`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              network: CHAIN_ID_NAMES[chainId],
              address: account,
              orders,
            }),
          }
        )
        if (!cancelled) {
          const json = await res.json()
          if (json && json.success) {
            json.data.forEach((order: { [key: string]: any }) => {
              dispatch({
                type: 'TRANSACTION_ORDER_STATUS',
                chainId,
                from: account,
                hash: order.transactionHash,
                nonce: order.nonce,
                orderStatus: {
                  type: order.type,
                  validAfterTimestamp: order.validAfterTimestamp,
                  status: order.status,
                  errorMessage: order.errorMessage,
                },
                amounts: { ...order.amounts, type: order.type && order.type.toUpperCase() },
                ethRefund: order.ethRefund,
                executionHash: order.executionHash,
              })
            })
          }
        }
      } catch (err) {
        console.error(err)
      }
    }
    updateOrderStatuses()
    const interval = setInterval(updateOrderStatuses, 30 * 1000)
    return () => {
      cancelled = true
      clearInterval(interval)
    }
  }, [chainId, account, JSON.stringify(orders)]) // have to convert orders to string for equality check, otherwise always fails

  return <TransactionsContext.Provider value={{ transactions, addTransaction }} children={children} />
}

function shouldCheck(blockNumber: number, tx: StoredTransaction): boolean {
  if (tx.receipt) return false
  if (tx.droppedAndReplaced) return false
  if (!tx.lastCheckedBlockNumber) return true
  const blocksSinceCheck = blockNumber - tx.lastCheckedBlockNumber
  if (blocksSinceCheck < 1) return false
  const minutesPending = (Date.now() - tx.submittedAt) / 1000 / 60
  if (minutesPending > 60) {
    // every 10 blocks if pending for longer than an hour
    return blocksSinceCheck > 9
  } else if (minutesPending > 5) {
    // every 3 blocks if pending more than 5 minutes
    return blocksSinceCheck > 2
  } else {
    // otherwise every block
    return true
  }
}
